import React, { useState, useEffect } from 'react';
import adminService from '../../api/adminService';
import { useAuth } from '../../contexts/KeycloakContext';
import { isSuperadmin } from '../../utils/roleUtils';
import './AdminDashboard.css';
import '../workspace/Members.css';
import Toast from '../../ui/Toast';
import Pagination from '../../ui/Pagination';
import Banner from '../../ui/Banner';

const UserManagement = ({ activeTab, setActiveTab }) => {
  const { keycloak, authenticated } = useAuth();
  const userIsSuperadmin = authenticated && isSuperadmin(keycloak);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

  // Search and Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortConfig, setSortConfig] = useState({ sortBy: 'username', sortOrder: 'asc' });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageInput, setPageInput] = useState('1');

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole, filterStatus, sortConfig, itemsPerPage]);

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user', // default role
  });

  // Edit User State
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await adminService.getUsers();
      // Transform Keycloak data to match our UI needs (mocking missing fields for now)
      const transformedUsers = data.map((user) => {
        const orgAttr = user.attributes?.organization?.[0];
        return {
          ...user,
          organization: !orgAttr || orgAttr === 'Unassigned' ? 'Personal' : orgAttr,
          role: user.attributes?.role?.[0]
            ? user.attributes.role[0].charAt(0).toUpperCase() + user.attributes.role[0].slice(1)
            : 'User',
          status: !user.enabled ? 'inactive' : !user.emailVerified ? 'pending' : 'active',
          lastActive: '1 Hour ago', // Mock data
        };
      });
      setUsers(transformedUsers);
    } catch (err) {
      setToast({ show: true, message: err.message, type: 'error' });
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await adminService.createUser(formData);
      // Reset form
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'user',
      });
      setShowCreateForm(false);

      setToast({
        show: true,
        message: response.activationEmailSent
          ? `User created successfully. A verification email has been sent to ${response.email || formData.email}.`
          : 'User created successfully, but the verification email could not be sent. Please check the email settings and try again.',
        type: response.activationEmailSent ? 'success' : 'error',
      });

      await fetchUsers(); // Refresh list to show new user
    } catch (err) {
      console.error('Error creating user:', err);

      const status = err.response?.status;
      const backendMessage = err.response?.data?.message;

      if (status === 409) {
        // Email / username already exists
        setToast({
          show: true,
          message:
            backendMessage || 'Email or username already exists. Please use a different one.',
          type: 'error',
        });
      } else {
        setToast({
          show: true,
          message: backendMessage || err.message || 'Failed to create user. Please try again.',
          type: 'error',
        });
      }
    }
  };

  const handleSort = (field) => {
    const isAsc = sortConfig.sortBy === field && sortConfig.sortOrder === 'asc';
    setSortConfig({
      sortBy: field,
      sortOrder: isAsc ? 'desc' : 'asc',
    });
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortConfig.sortBy !== field)
      return (
        <i
          className="fas fa-sort"
          style={{ marginLeft: '4px', opacity: 0.3, fontSize: '10px' }}
        ></i>
      );
    return sortConfig.sortOrder === 'asc' ? (
      <i
        className="fas fa-sort-up"
        style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px' }}
      ></i>
    ) : (
      <i
        className="fas fa-sort-down"
        style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px' }}
      ></i>
    );
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await adminService.deleteUser(userId);
      setToast({ show: true, message: 'User deleted successfully', type: 'success' });
      await fetchUsers();
    } catch (err) {
      setToast({ show: true, message: err.message || 'Failed to delete user', type: 'error' });
      console.error('Error deleting user:', err);
    }
  };

  const startEditUser = (user) => {
    setEditingUser({
      id: user.id,
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      organization: user.organization,
      role: user.role.toLowerCase(),
    });
    setShowEditForm(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      // Convert role to lowercase for backend (User -> user, Admin -> admin, Superadmin -> superadmin)
      const roleValue = editingUser.role.toLowerCase();

      await adminService.updateUser(editingUser.id, {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        attributes: {
          organization: [editingUser.organization],
          role: [roleValue],
        },
      });

      setShowEditForm(false);
      setEditingUser(null);
      setToast({ show: true, message: 'User updated successfully', type: 'success' });
      await fetchUsers();
    } catch (err) {
      setToast({ show: true, message: err.message || 'Failed to update user', type: 'error' });
    }
  };

  // Filter users based on search and selected filters
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase());

    // Role filter implementation (mock logic if needed)
    const matchesRole =
      filterRole === 'all' || user.role.toLowerCase() === filterRole.toLowerCase();
    const matchesStatus =
      filterStatus === 'all' || user.status.toLowerCase() === filterStatus.toLowerCase();

    return matchesSearch && matchesRole && matchesStatus;
  });

  const sortedUsers = [...filteredUsers].sort((left, right) => {
    const { sortBy, sortOrder } = sortConfig;
    const direction = sortOrder === 'asc' ? 1 : -1;

    const getComparable = (user) => {
      switch (sortBy) {
        case 'organization':
          return (user.organization || '').toLowerCase();
        case 'role':
          return (user.attributes?.role?.[0] || user.role || '').toLowerCase();
        case 'status':
          return (user.status || '').toLowerCase();
        case 'email':
          return (user.email || '').toLowerCase();
        default:
          return (user.username || '').toLowerCase();
      }
    };

    const leftValue = getComparable(left);
    const rightValue = getComparable(right);

    if (leftValue < rightValue) return -1 * direction;
    if (leftValue > rightValue) return 1 * direction;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / itemsPerPage));
  const indexOfLastUser = currentPage * itemsPerPage;
  const indexOfFirstUser = indexOfLastUser - itemsPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);

  const columns = [
    { key: 'username', label: 'User', sortable: true, width: '35%' },
    { key: 'organization', label: 'Organization', sortable: true, width: '25%' },
    { key: 'role', label: 'Role', sortable: true, width: '15%' },
    { key: 'status', label: 'Status', sortable: true, width: '15%' },
    userIsSuperadmin && { key: 'actions', label: 'Actions', className: 'col-act', width: '10%' },
  ].filter(Boolean);

  const renderUserRow = (user) => {
    const userRole = user.attributes?.role?.[0] || 'user';
    const isAdminUser = userRole === 'admin' || userRole === 'superadmin';
    const isSelf = user.id === keycloak?.tokenParsed?.sub;
    const canEditUser = !isSelf && (userIsSuperadmin || !isAdminUser);

    return (
      <tr key={user.id}>
        <td className="col-member">
          <div className="tm-member-cell">
            <div
              className="tm-avatar"
              style={{
                background:
                  userRole === 'superadmin'
                    ? '#2563eb'
                    : userRole === 'admin'
                      ? '#3b82f6'
                      : '#64748b',
              }}
            >
              {user.username?.substring(0, 2).toUpperCase() || '??'}
            </div>
            <div className="tm-member-info" style={{ overflow: 'hidden' }}>
              <span
                className="tm-name"
                style={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                }}
              >
                {user.username}
              </span>
              <span
                className="tm-email"
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                }}
              >
                {user.email || '—'}
              </span>
            </div>
          </div>
        </td>
        <td style={{ overflow: 'hidden' }}>
          <span
            className="tm-org-text"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }}
          >
            {user.organization}
          </span>
        </td>
        <td className="col-role">
          <span
            className={`tm-role-pill tm-role-${userRole === 'superadmin' ? 'organization_admin' : userRole === 'admin' ? 'organization_admin' : 'organization_user'}`}
            style={{ textTransform: 'capitalize' }}
          >
            {userRole === 'organization_user'
              ? 'Member'
              : userRole === 'organization_admin' || userRole === 'admin'
                ? 'Admin'
                : userRole === 'superadmin'
                  ? 'Superadmin'
                  : userRole}
          </span>
        </td>
        <td className="col-credit">
          <span
            className={`tm-credit-badge tm-credit-${user.status === 'active' ? 'active' : user.status === 'pending' ? 'pending' : 'inactive'}`}
            style={{ textTransform: 'capitalize' }}
          >
            {user.status === 'pending' ? 'Unverified' : user.status}
          </span>
        </td>
        {userIsSuperadmin && (
          <td className="col-act" style={{ textAlign: 'right' }}>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {canEditUser ? (
                <>
                  <button
                    className="tm-remove-btn"
                    title="Edit User"
                    onClick={() => startEditUser(user)}
                  >
                    <i className="fas fa-pen"></i>
                  </button>
                  <button
                    className="tm-remove-btn"
                    title="Delete User"
                    onClick={() => handleDeleteUser(user.id)}
                    style={{ color: '#ef4444' }}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </>
              ) : (
                <span style={{ color: '#94a3b8', fontSize: '12px', fontStyle: 'italic' }}>
                  No permission
                </span>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  };

  if (loading)
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        <div className="spinner" style={{ marginBottom: '10px' }}></div>
        <p>Loading users...</p>
      </div>
    );

  return (
    <div className="tm-page" style={{ padding: '0 0 40px 0' }}>
      <Banner
        title="User Management"
        subtitle="Manage user accounts, roles, and access across your platform."
        type="page-header"
      />

      <div className="tm-section-tabs">
        <button
          className={`tm-section-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <i className="fas fa-users"></i> All Users
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'audit-logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('audit-logs')}
        >
          <i className="fas fa-history"></i> Audit Logs
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'platform-activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('platform-activity')}
        >
          <i className="fas fa-chart-line"></i> Platform Activity
        </button>
      </div>
      <div className="tm-horizontal-divider"></div>

      <div className="animate-fade-in" style={{ padding: '12px 0' }}>
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )}

        {/* Edit User Modal Overlay */}
        {showEditForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <div
              className="animate-fade-in"
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                width: '500px',
                maxWidth: '90%',
                padding: '24px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Edit User</h3>
              <form onSubmit={handleUpdateUser}>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" value={editingUser.email} disabled />
                  <small
                    style={{
                      color: '#64748b',
                      fontSize: '12px',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    This email is fixed and cannot be changed here.
                  </small>
                </div>
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editingUser.firstName}
                    onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editingUser.lastName}
                    onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-input"
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    disabled={!userIsSuperadmin}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    {userIsSuperadmin && <option value="superadmin">Superadmin</option>}
                  </select>
                  {!userIsSuperadmin && (
                    <small
                      style={{
                        color: '#64748b',
                        fontSize: '12px',
                        marginTop: '4px',
                        display: 'block',
                      }}
                    >
                      Only superadmins can change roles
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Organization</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editingUser.organization}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, organization: e.target.value })
                    }
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    marginTop: '20px',
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEditForm(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="tm-toolbar">
          <div className="tm-toolbar-group tm-toolbar-grow">
            <label className="tm-toolbar-label">Search</label>
            <div className="tm-toolbar-search-wrap">
              <i className="fas fa-search tm-toolbar-search-icon"></i>
              <input
                className="tm-toolbar-input"
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="tm-toolbar-clear" onClick={() => setSearchTerm('')}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
          <div className="tm-toolbar-group">
            <label className="tm-toolbar-label">Status</label>
            <select
              className="tm-toolbar-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending verification</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="tm-toolbar-group">
            <label className="tm-toolbar-label">Role</label>
            <select
              className="tm-toolbar-select"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="all">All roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>
          <div className="tm-toolbar-group" style={{ minWidth: '118px', flex: '0 0 118px' }}>
            <label className="tm-toolbar-label" style={{ opacity: 0 }}>
              &nbsp;
            </label>
            <button
              className="tm-btn tm-btn-outline"
              style={{ width: '100%', justifyContent: 'center', height: '32px' }}
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
                setFilterRole('all');
                setSortConfig({ sortBy: 'username', sortOrder: 'asc' });
                setCurrentPage(1);
                setPageInput('1');
              }}
            >
              <i className="fas fa-times"></i> Clear
            </button>
          </div>
          <div className="tm-toolbar-group" style={{ minWidth: '160px', flex: '0 0 160px' }}>
            <label className="tm-toolbar-label" style={{ opacity: 0 }}>
              &nbsp;
            </label>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className={`tm-btn ${showCreateForm ? 'tm-btn-outline' : 'tm-btn-primary'}`}
              style={{ width: '100%', justifyContent: 'center', height: '32px', gap: '6px' }}
            >
              {showCreateForm ? <i className="fas fa-times"></i> : <i className="fas fa-plus"></i>}
              {showCreateForm ? 'Cancel' : 'Create New User'}
            </button>
          </div>
        </div>

        <Pagination
          columns={columns}
          data={currentUsers}
          renderRow={renderUserRow}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          totalItems={sortedUsers.length}
          itemsName="users"
          sortBy={sortConfig.sortBy}
          sortOrder={sortConfig.sortOrder}
          onSort={handleSort}
          emptyState={
            <div className="tm-empty">
              <i className="fas fa-users-slash"></i>
              <p>No users found. Try adjusting your search or filters.</p>
            </div>
          }
        />
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <div
          className="tm-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowCreateForm(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="tm-modal"
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              width: '600px',
              maxWidth: '90%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
          >
            <div
              className="tm-modal-head"
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <i className="fas fa-user-plus"></i> Create New User
              </h3>
              <button
                className="tm-modal-close"
                onClick={() => setShowCreateForm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f1f5f9';
                  e.target.style.color = '#1e293b';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#64748b';
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form
              onSubmit={handleCreateUser}
              style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
            >
              <div
                className="tm-modal-body"
                style={{
                  padding: '24px',
                  overflowY: 'auto',
                  flex: 1,
                }}
              >
                <div className="tm-field" style={{ marginBottom: '20px' }}>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                    }}
                  >
                    Email Address <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="email"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontSize: '14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                    }}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onFocus={(e) => (e.target.style.borderColor = '#2563eb')}
                    onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                    required
                    autoFocus
                    placeholder="user@example.com"
                  />
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px',
                    marginBottom: '20px',
                  }}
                >
                  <div className="tm-field">
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                      }}
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        outline: 'none',
                      }}
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      onFocus={(e) => (e.target.style.borderColor = '#2563eb')}
                      onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                      placeholder="John"
                    />
                  </div>
                  <div className="tm-field">
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                      }}
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        outline: 'none',
                      }}
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      onFocus={(e) => (e.target.style.borderColor = '#2563eb')}
                      onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  {userIsSuperadmin && (
                    <div className="tm-field">
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#374151',
                        }}
                      >
                        Role
                      </label>
                      <select
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          fontSize: '14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          outline: 'none',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                        }}
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        onFocus={(e) => (e.target.style.borderColor = '#2563eb')}
                        onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                      >
                        <option value="user">Personal User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  )}
                </div>

                <div
                  className="tm-field-hint"
                  style={{
                    padding: '12px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '6px',
                    border: '1px solid #bae6fd',
                    fontSize: '13px',
                    color: '#0369a1',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <i className="fas fa-info-circle" style={{ marginTop: '2px', flexShrink: 0 }}></i>
                  <span>
                    User will receive an email to verify their account, set a username, and create
                    their own password.
                  </span>
                </div>
              </div>

              <div
                className="tm-modal-foot"
                style={{
                  padding: '16px 24px',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  backgroundColor: '#f8fafc',
                }}
              >
                <button
                  type="button"
                  className="tm-btn tm-btn-outline"
                  onClick={() => setShowCreateForm(false)}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tm-btn tm-btn-primary"
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#1d4ed8')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#2563eb')}
                >
                  <i className="fas fa-user-plus"></i> Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default UserManagement;
